import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { pill, scrollBody, actionBtn, card, ProgressHeader, SectionTitle, ToggleSwitch, WebEntryLayout } from './entryHelpers';
import { patientAuthFlow } from '../../auth/patientAuthFlow';

const allergies = ['None', 'Penicillin', 'Sulfa', 'Aspirin', 'NSAIDs'];
const conditions = ['None', 'Diabetes', 'Hypertension', 'Asthma', 'Heart Disease', 'Thyroid'];
const surgeries = ['None', 'Appendectomy', 'Hernia', 'Cardiac', 'Orthopedic', 'Other'];
const disabilities = ['None', 'Visual', 'Hearing', 'Mobility', 'Cognitive', 'Other'];
const pregnancyOptions = ['Not Applicable', 'Currently Pregnant', 'Planning'];


const addConditions = ['Stroke', 'Cancer', 'Thyroid Disorder', 'Mental Health', 'Genetic Disorder'];

const labelStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 600,
  color: T.textTer,
  fontFamily: T.font,
  marginBottom: 4,
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: 10,
  border: `1px solid ${T.border}`,
  background: T.ice,
  fontSize: 12,
  fontFamily: T.font,
  color: T.text,
  outline: 'none',
  boxSizing: 'border-box',
};

const infoPill: React.CSSProperties = {
  fontSize: 8,
  fontWeight: 500,
  color: T.textSec,
  background: T.ice,
  border: `1px solid ${T.border}`,
  borderRadius: 4,
  padding: '2px 6px',
  fontFamily: T.font,
};

export default function PatRegHealthScreen() {
  const navigate = useNavigate();
  const [selectedAllergy, setSelectedAllergy] = useState('None');
  const [selectedCondition, setSelectedCondition] = useState('None');
  const [medicines, setMedicines] = useState('');
  const [selectedSurgery, setSelectedSurgery] = useState('None');
  const [selectedDisability, setSelectedDisability] = useState('None');
  const [selectedPregnancy, setSelectedPregnancy] = useState('Not Applicable');
  const [smoking, setSmoking] = useState(false);
  const [alcohol, setAlcohol] = useState(false);
  const [addedConditions, setAddedConditions] = useState<string[]>([]);

  return (
    <WebEntryLayout>
    <div style={{ flex: 1, background: T.white, display: 'flex', flexDirection: 'column', fontFamily: T.font }}>
      <ProgressHeader title="Health Details" step={4} total={8} onBack={() => navigate(-1)} />

      <div style={scrollBody}>
        {/* Known Allergies */}
        <div>
          <div style={labelStyle}>Known Allergies</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {allergies.map((a) => (
              <div
                key={a}
                onClick={() => setSelectedAllergy(a)}
                style={{ ...pill(a, T.danger, selectedAllergy === a), padding: '6px 12px', cursor: 'pointer' }}
              >
                {a}
              </div>
            ))}
          </div>
        </div>

        {/* Chronic Conditions */}
        <div>
          <div style={labelStyle}>Chronic Conditions</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {conditions.map((c) => (
              <div
                key={c}
                onClick={() => setSelectedCondition(c)}
                style={{ ...pill(c, T.purple, selectedCondition === c), cursor: 'pointer' }}
              >
                {c}
              </div>
            ))}
          </div>
        </div>

        {/* Current Medicines */}
        <div>
          <div style={labelStyle}>Current Medicines</div>
          <input
            type="text"
            placeholder="e.g. Metformin 500mg, Atorvastatin 10mg"
            value={medicines}
            onChange={(e) => setMedicines(e.target.value)}
            style={{ ...fieldStyle, color: T.textTer }}
          />
        </div>

        {/* Past Surgeries */}
        <div>
          <div style={labelStyle}>Past Surgeries</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {surgeries.map((s) => (
              <div
                key={s}
                onClick={() => setSelectedSurgery(s)}
                style={{ ...pill(s, T.navy, selectedSurgery === s), cursor: 'pointer' }}
              >
                {s}
              </div>
            ))}
          </div>
        </div>

        {/* Family Medical History — starts EMPTY; only what the patient
            actually selects is saved (as familyHistory, never as their own
            conditions). */}
        <div style={card()}>
          <SectionTitle text="Family Medical History (optional)" />
          <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, marginBottom: 8, lineHeight: 1.5 }}>
            Select conditions that run in your family. Leave empty if none or unsure.
          </div>

          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 8, paddingTop: 8 }}>
            {addConditions.map((ac) => (
              <div key={ac} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontSize: 9, color: addedConditions.includes(ac) ? T.text : T.textSec, fontFamily: T.font, fontWeight: addedConditions.includes(ac) ? 600 : 400 }}>{ac}</span>
                <span
                  onClick={() => setAddedConditions(prev => prev.includes(ac) ? prev.filter(c => c !== ac) : [...prev, ac])}
                  style={{ fontSize: 9, color: addedConditions.includes(ac) ? T.danger : T.teal, fontWeight: 600, cursor: 'pointer', fontFamily: T.font }}
                >
                  {addedConditions.includes(ac) ? '✕ Remove' : '+ Add'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Lifestyle */}
        <div>
          <div style={labelStyle}>Lifestyle</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: T.text, fontFamily: T.font }}>Smoking</div>
                <div style={{ fontSize: 8, color: T.textSec, fontFamily: T.font }}>{smoking ? 'Smoker' : 'Non-smoker'}</div>
              </div>
              <div onClick={() => setSmoking(!smoking)} style={{ cursor: 'pointer' }}><ToggleSwitch on={smoking} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: T.text, fontFamily: T.font }}>Alcohol</div>
                <div style={{ fontSize: 8, color: T.textSec, fontFamily: T.font }}>{alcohol ? 'Occasional' : 'Non-drinker'}</div>
              </div>
              <div onClick={() => setAlcohol(!alcohol)} style={{ cursor: 'pointer' }}><ToggleSwitch on={alcohol} /></div>
            </div>
          </div>
        </div>

        {/* Disability Status */}
        <div>
          <div style={labelStyle}>Disability Status</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {disabilities.map((d) => (
              <div
                key={d}
                onClick={() => setSelectedDisability(d)}
                style={{ ...pill(d, T.navy, selectedDisability === d), cursor: 'pointer' }}
              >
                {d}
              </div>
            ))}
          </div>
        </div>

        {/* Pregnancy Status */}
        <div>
          <div style={labelStyle}>Pregnancy Status</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {pregnancyOptions.map((p) => (
              <div
                key={p}
                onClick={() => setSelectedPregnancy(p)}
                style={{ ...pill(p, T.purple, selectedPregnancy === p), cursor: 'pointer' }}
              >
                {p}
              </div>
            ))}
          </div>
        </div>

        {/* Next Button */}
        <button
          onClick={() => {
            patientAuthFlow.completeHealth({
              allergies: selectedAllergy === 'None' ? [] : [selectedAllergy],
              conditions: selectedCondition === 'None' ? [] : [selectedCondition],
              medicines,
              surgeries: selectedSurgery === 'None' ? [] : [selectedSurgery],
              // Family conditions are the FAMILY's history — never merged
              // into the patient's own condition list.
              familyHistory: addedConditions,
            });
            navigate('/patient/register/emergency');
          }}
          style={{
            ...actionBtn('Next', T.teal, T.white),
            width: '100%',
            padding: 13,
            borderRadius: 12,
            fontSize: 13,
          }}
        >
          Next
        </button>
      </div>
    </div>
    </WebEntryLayout>
  );
}
